import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, ChevronRight, ArrowLeft, Home, Shirt,
  TrendingUp, Scissors, User, Heart, Headphones, Phone, MessageCircle,
  Award, Leaf, ShieldCheck,
} from "lucide-react";

const ACCENT = "#7A2E35"; // deep maroon, used sparingly
const PHONE = "917021703587";
const PHONE_DISPLAY = "+91 70217 03587";

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

const MobileMenu = ({ open, onClose, products = [], categories = [], customer }) => {
  const navigate = useNavigate();
  const [level, setLevel] = useState(0); // 0 main, 1 blouses-by, 2 facet values
  const [facet, setFacet] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (open) { setLevel(0); setFacet(null); setHelpOpen(false); }
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (to) => { onClose(); navigate(to); };
  const openWhatsapp = (text) => {
    onClose();
    window.open(`https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  // Build "Blouses By …" facet groups from real product data
  const facetGroups = useMemo(() => {
    const necklines = uniq(products.map((p) => p.neckline));
    const sleeves = uniq(products.map((p) => p.sleeves));
    const fabrics = uniq(products.map((p) => p.fabricType));
    const colors = uniq(products.flatMap((p) => [p.primaryColor, ...(p.secondaryColors || [])]));
    const budget = [
      { label: "Under ₹2,000", value: "0-2000" },
      { label: "₹2,000 – ₹5,000", value: "2000-5000" },
      { label: "₹5,000 – ₹10,000", value: "5000-10000" },
      { label: "Above ₹10,000", value: "10000-" },
    ];
    const collections = (categories || []).map((c) => ({ label: c.name, value: c.id }));
    return [
      { id: "collection", title: "Shop by Collection", param: "occasion", values: collections },
      { id: "neckline", title: "Blouses By Neck Style", param: "neckline", values: necklines.map((v) => ({ label: v, value: v })) },
      { id: "sleeves", title: "Blouses By Sleeve Style", param: "sleeves", values: sleeves.map((v) => ({ label: v, value: v })) },
      { id: "fabric", title: "Blouses By Fabric", param: "fabric", values: fabrics.map((v) => ({ label: v, value: v })) },
      { id: "color", title: "Blouses By Color", param: "color", values: colors.map((v) => ({ label: v, value: v })) },
      { id: "budget", title: "Blouses By Budget", param: "budget", values: budget },
    ].filter((g) => g.values.length > 0);
  }, [products, categories]);

  const primary = [
    { icon: Home, label: "New In", to: "/catalog?new=true" },
    { icon: Shirt, label: "Blouses", level: 1 },
    { icon: TrendingUp, label: "Trending", to: "/catalog?tag=Trending" },
    { icon: Scissors, label: "Custom Tailoring", to: "/schedule-call" },
  ];

  const trust = [
    { icon: Award, label: "Premium\nQuality" },
    { icon: Leaf, label: "Handcrafted\nin India" },
    { icon: ShieldCheck, label: "Secure\nShopping" },
  ];

  const rowCls = "group w-full flex items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-[#F2EFEB]";
  const labelCls = "flex-1 font-body text-[15px] tracking-wide text-[#1A1A1A] transition-colors group-hover:text-[#7A2E35]";

  const headerTitle = level === 1 ? "Blouses" : level === 2 ? (facet?.title?.replace("Blouses By ", "").replace("Shop by ", "") || "") : "";

  return (
    <div
      className={`fixed inset-0 z-[60] md:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!open}
      data-testid="mobile-menu"
    >
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      <div
        className={`absolute left-0 top-0 h-full w-[88%] max-w-[400px] bg-white rounded-r-2xl shadow-[0_0_40px_rgba(0,0,0,0.10)] flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-menu-drawer"
      >
        {/* Header */}
        <div className="flex items-center h-16 px-4 border-b border-[#ECE8E1] flex-shrink-0">
          {level > 0 ? (
            <button
              onClick={() => { setLevel((l) => l - 1); }}
              className="p-1.5 -ml-1.5 text-[#1A1A1A]"
              data-testid="mobile-menu-back"
            >
              <ArrowLeft size={20} strokeWidth={1.6} />
            </button>
          ) : (
            <span className="w-7" />
          )}
          <span className="flex-1 text-center font-heading text-lg tracking-tight text-[#1A1A1A]">
            {headerTitle}
          </span>
          <button onClick={onClose} data-testid="mobile-menu-close" className="p-1.5 -mr-1.5 text-[#1A1A1A]">
            <X size={22} strokeWidth={1.6} />
          </button>
        </div>

        {/* 3-level push panels */}
        <div className="flex-1 overflow-hidden">
          <div
            className="flex h-full w-[300%] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ transform: `translateX(-${(level * 100) / 3}%)` }}
          >
            {/* LEVEL 0 — MAIN */}
            <div className="w-1/3 h-full overflow-y-auto flex flex-col">
              <nav className="flex-1 py-3">
                <ul>
                  {primary.map((item) => (
                    <li key={item.label}>
                      <button
                        onClick={() => (item.level ? setLevel(1) : go(item.to))}
                        className={rowCls}
                        data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <item.icon size={20} strokeWidth={1.4} style={{ color: ACCENT }} />
                        <span className={labelCls}>{item.label}</span>
                        {item.level && <ChevronRight size={17} className="text-[#B8B2A8]" />}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="my-2.5 mx-4 border-t border-[#ECE8E1]" />

                <ul>
                  <li>
                    <button onClick={() => go(customer ? "/account" : "/login")} className={rowCls} data-testid="menu-item-my-account">
                      <User size={20} strokeWidth={1.4} style={{ color: ACCENT }} />
                      <span className="flex-1">
                        <span className="block font-body text-[15px] tracking-wide text-[#1A1A1A] transition-colors group-hover:text-[#7A2E35]">
                          {customer ? (customer.name?.trim() || "My Account") : "My Account"}
                        </span>
                        {customer && (
                          <span className="block text-xs text-[#68645E] mt-0.5" data-testid="menu-account-phone">+91 {customer.phone}</span>
                        )}
                      </span>
                      <ChevronRight size={17} className="text-[#B8B2A8]" />
                    </button>
                  </li>
                  <li>
                    <button onClick={() => go(customer ? "/account" : "/login?redirect=/account")} className={rowCls} data-testid="menu-item-wishlist">
                      <Heart size={20} strokeWidth={1.4} style={{ color: ACCENT }} />
                      <span className={labelCls}>Wishlist</span>
                    </button>
                  </li>
                  <li>
                    <button onClick={() => setHelpOpen((v) => !v)} className={rowCls} data-testid="menu-item-help">
                      <Headphones size={20} strokeWidth={1.4} style={{ color: ACCENT }} />
                      <span className={labelCls}>Help &amp; Support</span>
                      <ChevronRight size={17} className={`text-[#B8B2A8] transition-transform duration-300 ${helpOpen ? "rotate-90" : ""}`} />
                    </button>
                    {helpOpen && (
                      <div className="pl-[52px] pr-4 pb-3 pt-0.5 space-y-3" data-testid="help-contact">
                        <a href={`tel:+${PHONE}`} className="flex items-center gap-2.5 text-sm text-[#68645E] hover:text-[#7A2E35] transition-colors">
                          <Phone size={15} style={{ color: ACCENT }} /> {PHONE_DISPLAY}
                        </a>
                        <a href={`https://wa.me/${PHONE}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-sm text-[#68645E] hover:text-[#7A2E35] transition-colors">
                          <MessageCircle size={15} style={{ color: ACCENT }} /> WhatsApp Us
                        </a>
                      </div>
                    )}
                  </li>
                </ul>
              </nav>

              {/* Trust strip */}
              <div className="border-t border-[#ECE8E1] bg-[#FAF8F5] px-4 py-5">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {trust.map((t) => (
                    <div key={t.label} className="flex flex-col items-center gap-1.5">
                      <t.icon size={18} strokeWidth={1.4} style={{ color: ACCENT }} />
                      <span className="text-[10px] leading-tight text-[#68645E] whitespace-pre-line tracking-wide">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* LEVEL 1 — BLOUSES BY … */}
            <div className="w-1/3 h-full overflow-y-auto py-3">
              <ul>
                <li>
                  <button onClick={() => go("/catalog")} className="group w-full flex items-center justify-between px-4 py-4 text-left border-b border-[#F0ECE5] transition-colors hover:bg-[#F2EFEB]" data-testid="blouses-shop-all">
                    <span className="font-body text-[15px] tracking-wide text-[#1A1A1A] group-hover:text-[#7A2E35]">Shop All Blouses</span>
                    <ChevronRight size={16} className="text-[#B8B2A8]" />
                  </button>
                </li>
                {facetGroups.map((g) => (
                  <li key={g.id}>
                    <button
                      onClick={() => { setFacet(g); setLevel(2); }}
                      className="group w-full flex items-center justify-between px-4 py-4 text-left border-b border-[#F0ECE5] transition-colors hover:bg-[#F2EFEB]"
                      data-testid={`blouses-by-${g.id}`}
                    >
                      <span className="font-body text-[15px] tracking-wide text-[#1A1A1A] group-hover:text-[#7A2E35]">{g.title}</span>
                      <ChevronRight size={16} className="text-[#B8B2A8]" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* LEVEL 2 — FACET VALUES */}
            <div className="w-1/3 h-full overflow-y-auto py-3">
              <p className="px-4 pb-2 overline text-[10px]">Choose</p>
              <ul>
                {facet?.values?.map((v) => (
                  <li key={v.value}>
                    <button
                      onClick={() => go(`/catalog?${facet.param}=${encodeURIComponent(v.value)}`)}
                      className="group w-full flex items-center justify-between px-4 py-4 text-left border-b border-[#F0ECE5] transition-colors hover:bg-[#F2EFEB]"
                      data-testid={`facet-value-${String(v.value).toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="font-body text-[15px] tracking-wide text-[#1A1A1A] group-hover:text-[#7A2E35]">{v.label}</span>
                      <ChevronRight size={16} className="text-[#B8B2A8]" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
